// Money → Bookkeeper pack: one month's bills and payments for the bookkeeper (Wave), as one zip: bills.csv,
// payments.csv, the bills' invoices and the payments' receipts named by date and vendor, a summary. The preview here is
// worked out from synced rows with the same shared/bills.ts bookkeeperPack() the zip uses, so they agree.
import { AlertTriangle, Download } from "lucide-react";
import { useMemo, useState } from "react";
import { BILL_STATUS_LABELS, bookkeeperPack, PAYMENT_METHOD_LABELS, type PackBill, type PackPayment, unconfirmedText } from "../../../../shared/bills.ts";
import { planToday } from "../../../../shared/day.ts";
import { amount, dayOf, useBillIndex } from "../../lib/bills.ts";
import { shortDate } from "../../lib/costs.ts";
import { Link, setSearch, useSearch } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Badge, Select } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { openPane } from "../../ui/SidePane.tsx";

const lastMonths = (n: number) => {
  const [y, m] = planToday().split("-").map(Number);
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(Date.UTC(y, m - 1 - i, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
  });
};
const monthName = (ym: string) => new Date(`${ym}-15T12:00:00Z`).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
const money = (m: Record<string, number>) =>
  Object.entries(m)
    .map(([cur, n]) => amount(n, cur))
    .join(" + ") || "$0.00";

export function BookkeeperPage() {
  const params = useSearch();
  const months = lastMonths(18);
  const month = params.get("month") ?? months[1];
  const ix = useBillIndex();
  const transfers = useRows("wise_transfer");
  const pack = useMemo(() => bookkeeperPack(ix, month, dayOf, undefined, transfers), [ix, month, transfers]);
  const missing = pack.missing.invoices.length + pack.missing.receipts.length;
  const unconfirmed = unconfirmedText(pack.unconfirmed, monthName(month).split(" ")[0]);
  const billRows = useMemo<BillRow[]>(() => pack.bills.map((r) => ({ ...r, id: r.bill.id })), [pack]);
  const paymentRows = useMemo<PaymentRow[]>(() => pack.payments.map((r) => ({ ...r, id: r.payment.id })), [pack]);
  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-8">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold">Bookkeeper pack</h1>
          <Select
            aria-label="Month"
            value={month}
            options={months.map((m) => ({ value: m, label: monthName(m) }))}
            onChange={(e) => setSearch({ month: e.target.value })}
            className="h-8 w-48"
          />
          <a
            href={`/api/bookkeeper-pack?month=${month}`}
            className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-md bg-accent px-3 font-medium text-white hover:bg-accent-hover"
            download
            onClick={(e) => {
              if (unconfirmed && !confirm(`${unconfirmed}: payments.csv will leave them out. Download an incomplete pack anyway?`)) e.preventDefault();
            }}
          >
            <Download className="size-4" /> Download the zip
          </a>
        </div>
        <p className="mt-1 max-w-3xl text-[13px] text-muted">
          For the bookkeeper (Wave): bills dated in {monthName(month)} and payments made in it. bills.csv and payments.csv, each bill's invoice in bills/, each
          payment's receipt in receipts/, named by date, vendor and number. Every Wise payment's reference names the vendor, PO and bill, so its bank-feed line
          explains itself.
        </p>
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Bills" value={String(pack.bills.length)} sub={money(pack.totals.billed)} />
          <Stat label="Payments" value={String(pack.payments.length)} sub={money(pack.totals.paid)} />
          <Stat label="Fees" value={money(pack.totals.fees)} sub="Wise's and banks' fees on top" />
          <Stat label="Files" value={String(pack.files.length)} sub={missing ? `${missing} missing` : "Nothing missing"} warn={missing > 0} />
        </div>
        {unconfirmed && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-soft px-4 py-2.5">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-danger" />
            <span>
              <span className="font-medium">{unconfirmed}</span>, so payments.csv leaves them out and the pack isn't complete.{" "}
              <Link
                to={`/bills?tab=wise&show=${pack.unconfirmed.toConfirm ? "suggested" : pack.unconfirmed.noMatch ? "no_match" : "new"}`}
                className="whitespace-nowrap text-accent-text hover:underline"
              >
                {pack.unconfirmed.onTheirWay === pack.unconfirmed.count ? "See them in Wise history" : "Confirm them in Wise history"}
              </Link>
            </span>
          </div>
        )}
        {(missing > 0 || pack.draftsLeftOut > 0) && (
          <div className="mt-3 rounded-lg border border-warn/40 bg-warn-soft/40 px-4 py-2.5 text-[12px]">
            {pack.missing.invoices.length > 0 && (
              <div className="flex items-start gap-1.5">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-warn" />
                <span>Bills without their invoice file yet: {pack.missing.invoices.join(" · ")}</span>
              </div>
            )}
            {pack.missing.receipts.length > 0 && (
              <div className="mt-1 flex items-start gap-1.5">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-warn" />
                <span>Payments without a receipt yet: {pack.missing.receipts.join(" · ")}</span>
              </div>
            )}
            {pack.draftsLeftOut > 0 && (
              <div className="mt-1 text-muted">
                {pack.draftsLeftOut} draft {pack.draftsLeftOut === 1 ? "bill is" : "bills are"} left out until approved (Bills & payments → To approve).
              </div>
            )}
          </div>
        )}

        <div className="mt-6 mb-1.5 flex items-center gap-2">
          <h2 className="text-[11px] font-medium tracking-wide text-faint uppercase">bills.csv</h2>
        </div>
        <div className="rounded-md bg-surface">
          <DataTable
            fit={20}
            rows={billRows}
            columns={billColumns}
            defaultSort={{ key: "date" }}
            onOpen={(r) => openPane({ kind: "bill", id: r.bill.id }, { root: true })}
            exportName="Bookkeeper bills"
            pageFilters={false}
            rowNoun={["bill", "bills"]}
            empty={<div className="p-4 text-center text-muted">No bills dated in {monthName(month)}.</div>}
          />
        </div>

        <div className="mt-6 mb-1.5 flex items-center gap-2">
          <h2 className="text-[11px] font-medium tracking-wide text-faint uppercase">payments.csv</h2>
        </div>
        <div className="rounded-md bg-surface">
          <DataTable
            fit={20}
            rows={paymentRows}
            columns={paymentColumns}
            defaultSort={{ key: "date" }}
            exportName="Bookkeeper payments"
            pageFilters={false}
            rowNoun={["payment", "payments"]}
            empty={<div className="p-4 text-center text-muted">No payments made in {monthName(month)}.</div>}
          />
        </div>
        <p className="mt-4 text-[12px] text-faint">
          Posting paid bills straight into Wave is possible later (Wave's moneyTransactionCreate), once the bookkeeper confirms Wave doesn't already get Wise
          through a bank connection: that would count every payment twice.
        </p>
      </div>
    </div>
  );
}

type BillRow = PackBill & { id: string };
type PaymentRow = PackPayment & { id: string };

const billDay = (b: PackBill["bill"]) => (b.bill_date ? String(b.bill_date).slice(0, 10) : dayOf(b.created_at));

// bills.csv as the pack has it: dated, number, vendor, their invoice, POs, status, amount, whether its invoice is in it
const billColumns: Column<BillRow>[] = [
  {
    key: "date",
    header: "Dated",
    width: "96px",
    value: (r) => billDay(r.bill),
    cell: (r) => <span className="text-[12px] text-muted">{shortDate(billDay(r.bill))}</span>,
  },
  {
    key: "number",
    header: "Bill",
    width: "90px",
    value: (r) => Number(r.bill.number.replace(/\D/g, "")) || 0,
    text: (r) => r.bill.number,
    cell: (r) => <span className="font-mono text-[12px]">{r.bill.number}</span>,
  },
  { key: "vendor", header: "Vendor", width: "minmax(130px,1fr)", cell: (r) => <span className="truncate font-medium">{r.vendor}</span> },
  {
    key: "invoice",
    header: "Their invoice",
    width: "minmax(110px,1fr)",
    value: (r) => r.bill.invoice_number,
    cell: (r) => <span className="truncate font-mono text-[12px] text-muted">{r.bill.invoice_number}</span>,
  },
  {
    key: "pos",
    header: "POs",
    width: "minmax(90px,1fr)",
    value: (r) => r.pos.join(" "),
    cell: (r) => <span className="truncate text-[12px] text-muted">{r.pos.join(" ")}</span>,
  },
  {
    key: "status",
    header: "Status",
    width: "100px",
    value: (r) => BILL_STATUS_LABELS[r.bill.status],
    cell: (r) => <Badge tone={r.bill.status === "paid" ? "ok" : "accent"}>{BILL_STATUS_LABELS[r.bill.status]}</Badge>,
  },
  {
    key: "amount",
    header: "Amount",
    width: "110px",
    align: "right",
    value: (r) => Number(r.bill.amount),
    text: (r) => amount(Number(r.bill.amount), r.bill.currency),
    cell: (r) => <span className="font-mono">{amount(Number(r.bill.amount), r.bill.currency)}</span>,
  },
  {
    key: "file",
    header: "Invoice",
    width: "100px",
    align: "right",
    value: (r) => (r.files.length ? "invoice ✓" : "no invoice"),
    cell: (r) => <span className="text-[12px] text-muted">{r.files.length ? "invoice ✓" : <span className="text-warn">no invoice</span>}</span>,
  },
];

// payments.csv as the pack has it: paid on, number, vendor, how, reference, the bills, amount, fee, its receipt
const paymentColumns: Column<PaymentRow>[] = [
  {
    key: "date",
    header: "Paid on",
    width: "96px",
    value: (r) => String(r.payment.paid_on ?? "").slice(0, 10),
    cell: (r) => <span className="text-[12px] text-muted">{shortDate(r.payment.paid_on)}</span>,
  },
  {
    key: "number",
    header: "Payment",
    width: "90px",
    value: (r) => Number(r.payment.number.replace(/\D/g, "")) || 0,
    text: (r) => r.payment.number,
    cell: (r) => <span className="font-mono text-[12px]">{r.payment.number}</span>,
  },
  { key: "vendor", header: "Vendor", width: "minmax(130px,1fr)", cell: (r) => <span className="truncate font-medium">{r.vendor}</span> },
  {
    key: "method",
    header: "How",
    width: "100px",
    value: (r) => PAYMENT_METHOD_LABELS[r.payment.method],
    cell: (r) => <span className="truncate text-[12px] text-muted">{PAYMENT_METHOD_LABELS[r.payment.method]}</span>,
  },
  {
    key: "reference",
    header: "Reference",
    width: "minmax(110px,1fr)",
    value: (r) => r.payment.reference,
    cell: (r) => (
      <span className="truncate font-mono text-[12px] text-muted" title={r.payment.reference}>
        {r.payment.reference}
      </span>
    ),
  },
  {
    key: "bills",
    header: "Bills",
    width: "minmax(90px,1fr)",
    value: (r) => r.bills.map((b) => b.number).join(", "),
    cell: (r) => <span className="truncate text-[12px] text-muted">{r.bills.map((b) => b.number).join(", ")}</span>,
  },
  {
    key: "amount",
    header: "Amount",
    width: "110px",
    align: "right",
    value: (r) => Number(r.payment.amount),
    text: (r) => amount(Number(r.payment.amount), r.payment.currency),
    cell: (r) => <span className="font-mono">{amount(Number(r.payment.amount), r.payment.currency)}</span>,
  },
  {
    key: "fee",
    header: "Fee",
    width: "90px",
    align: "right",
    value: (r) => Number(r.payment.fee) || 0,
    text: (r) => (Number(r.payment.fee) ? amount(Number(r.payment.fee), r.payment.fee_currency) : ""),
    cell: (r) => <span className="font-mono text-[12px] text-muted">{Number(r.payment.fee) ? amount(Number(r.payment.fee), r.payment.fee_currency) : ""}</span>,
  },
  {
    key: "file",
    header: "Receipt",
    width: "100px",
    align: "right",
    value: (r) => (r.files.length ? "receipt ✓" : "no receipt"),
    cell: (r) => <span className="text-[12px] text-muted">{r.files.length ? "receipt ✓" : <span className="text-warn">no receipt</span>}</span>,
  },
];

function Stat({ label, value, sub, warn }: { label: string; value: string; sub: string; warn?: boolean }) {
  return (
    <div className="rounded-lg border border-line bg-surface px-4 py-3">
      <div className="text-[11px] font-medium tracking-wide text-faint uppercase">{label}</div>
      <div className="mt-1 font-mono text-lg font-semibold">{value}</div>
      <div className={warn ? "text-[12px] text-warn" : "text-[12px] text-muted"}>{sub}</div>
    </div>
  );
}
